import { ArrowLeft, ArrowRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[#71717A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</label>
);
const Input = ({ testid, ...props }) => <input data-testid={testid} className="brutalist-input" {...props} />;
const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h3>
);

export default function DomesticEICStep2({ formData, updateForm, onNext, onPrev }) {
  const set = (field) => (e) => updateForm({ [field]: e.target.value });

  return (
    <div className="form-container max-w-3xl mx-auto" data-testid="eic-step-2">
      <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Supply Characteristics & Earthing</h2>

      {/* Supply System */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <SectionTitle>Supply System</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="form-field">
            <Label>Earthing system</Label>
            <select data-testid="eic-supply-system" value={formData.supply_system} onChange={set("supply_system")} className="brutalist-input">
              <option value="">Select...</option>
              <option value="TN-S">TN-S</option>
              <option value="TN-C-S">TN-C-S</option>
              <option value="TT">TT</option>
              <option value="TN-C-S (PNB)">TN-C-S (PNB)</option>
            </select>
          </div>
          <div className="form-field">
            <Label>Number and type of live conductors</Label>
            <select data-testid="eic-supply-conductors" value={formData.supply_conductors} onChange={set("supply_conductors")} className="brutalist-input">
              <option value="">Select...</option>
              <option value="1-phase (2 wire)">1-phase (2 wire)</option>
              <option value="1-phase (3 wire)">1-phase (3 wire)</option>
              <option value="2-phase (3 wire)">2-phase (3 wire)</option>
              <option value="3-phase (4 wire)">3-phase (4 wire)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Supply Parameters */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <SectionTitle>Nature of Supply Parameters</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="form-field"><Label>Nominal voltage U (V)</Label><Input testid="eic-voltage" value={formData.nominal_voltage} onChange={set("nominal_voltage")} /></div>
          <div className="form-field"><Label>Frequency f (Hz)</Label><Input testid="eic-frequency" value={formData.nominal_frequency} onChange={set("nominal_frequency")} /></div>
          <div className="form-field"><Label>External Ze (Ω)</Label><Input testid="eic-ext-ze" value={formData.external_ze} onChange={set("external_ze")} /></div>
          <div className="form-field"><Label>PFC (kA)</Label><Input testid="eic-pfc" value={formData.pfc} onChange={set("pfc")} /></div>
        </div>
      </div>

      {/* Primary Supply */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <SectionTitle>Characteristics of Primary Supply</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="form-field"><Label>BS(EN)</Label><Input testid="eic-ps-bs" value={formData.primary_supply_bs} onChange={set("primary_supply_bs")} placeholder="e.g. BS 1361" /></div>
          <div className="form-field"><Label>Type</Label><Input testid="eic-ps-type" value={formData.primary_supply_type} onChange={set("primary_supply_type")} placeholder="e.g. Type 2" /></div>
          <div className="form-field"><Label>Rated current (A)</Label><Input testid="eic-ps-current" value={formData.primary_supply_rated_current} onChange={set("primary_supply_rated_current")} /></div>
          <div className="form-field"><Label>Short-circuit (kA)</Label><Input testid="eic-ps-sc" value={formData.primary_supply_short_circuit} onChange={set("primary_supply_short_circuit")} /></div>
        </div>
      </div>

      {/* Means of Earthing */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <SectionTitle>Means of Earthing</SectionTitle>
        <div className="flex gap-4 mb-3">
          {[{ v: "distributor", l: "Distributor's facility" }, { v: "electrode", l: "Installation earth electrode" }, { v: "other", l: "Other" }].map((opt) => (
            <label key={opt.v} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="earthing_means" value={opt.v} checked={formData.earthing_means === opt.v}
                onChange={(e) => updateForm({ earthing_means: e.target.value })} className="w-4 h-4 accent-[#00A3E0]" />
              <span className="text-sm">{opt.l}</span>
            </label>
          ))}
        </div>
        {formData.earthing_means === "electrode" && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
            <div className="form-field"><Label>Electrode type</Label><Input testid="eic-electrode-type" value={formData.earth_electrode_type} onChange={set("earth_electrode_type")} placeholder="e.g. rod(s)" /></div>
            <div className="form-field"><Label>Electrode resistance RA (Ω)</Label><Input testid="eic-electrode-ra" value={formData.earth_electrode_resistance} onChange={set("earth_electrode_resistance")} /></div>
            <div className="form-field"><Label>Location</Label><Input testid="eic-electrode-location" value={formData.electrode_location} onChange={set("electrode_location")} /></div>
          </div>
        )}
      </div>

      {/* Installation Particulars */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <SectionTitle>Particulars of Installation at the Origin</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div className="form-field"><Label>Measured Ze (Ω)</Label><Input testid="eic-measured-ze" value={formData.measured_ze} onChange={set("measured_ze")} /></div>
          <div className="form-field"><Label>Method</Label><Input testid="eic-ze-method" value={formData.ze_method} onChange={set("ze_method")} /></div>
          <div className="form-field"><Label>Earthing conductor material</Label><Input testid="eic-earth-material" value={formData.earthing_conductor_material} onChange={set("earthing_conductor_material")} /></div>
          <div className="form-field"><Label>Earthing conductor csa (mm²)</Label><Input testid="eic-earth-csa" value={formData.earthing_conductor_csa} onChange={set("earthing_conductor_csa")} /></div>
        </div>
        <div className="mb-3">
          <Label>Main protective bonding conductors</Label>
          <div className="flex flex-wrap gap-4 mt-1">
            {[{ field: "bonding_gas", label: "Gas" }, { field: "bonding_water", label: "Water" }, { field: "bonding_oil", label: "Oil" }, { field: "bonding_structural", label: "Structural steel" }].map((b) => (
              <label key={b.field} className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={formData[b.field]} onCheckedChange={(c) => updateForm({ [b.field]: !!c })}
                  className="w-4 h-4 border-2 border-[#0A0A0A] rounded-none data-[state=checked]:bg-[#00A3E0] data-[state=checked]:border-[#00A3E0]" />
                <span className="text-sm">{b.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="form-field"><Label>Supply conductor material</Label><Input testid="eic-supply-material" value={formData.supply_conductor_material} onChange={set("supply_conductor_material")} /></div>
          <div className="form-field"><Label>Supply conductor csa (mm²)</Label><Input testid="eic-supply-csa" value={formData.supply_conductor_csa} onChange={set("supply_conductor_csa")} /></div>
        </div>
      </div>

      {/* Main Switch */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <SectionTitle>Main Switch / Circuit Breaker / RCD</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="form-field"><Label>Type BS(EN)</Label><Input testid="eic-ms-type" value={formData.main_switch_type} onChange={set("main_switch_type")} /></div>
          <div className="form-field"><Label>Voltage (V)</Label><Input testid="eic-ms-voltage" value={formData.main_switch_voltage} onChange={set("main_switch_voltage")} /></div>
          <div className="form-field"><Label>Rated current (A)</Label><Input testid="eic-ms-current" value={formData.main_switch_rated_current} onChange={set("main_switch_rated_current")} /></div>
          <div className="form-field"><Label>No. of poles</Label><Input testid="eic-ms-poles" value={formData.num_poles} onChange={set("num_poles")} /></div>
          <div className="form-field"><Label>RCD IΔn (mA)</Label><Input testid="eic-ms-rcd-current" value={formData.main_rcd_current} onChange={set("main_rcd_current")} placeholder="N/A" /></div>
          <div className="form-field"><Label>RCD time (ms)</Label><Input testid="eic-ms-rcd-time" value={formData.main_rcd_time} onChange={set("main_rcd_time")} placeholder="N/A" /></div>
          <div className="form-field"><Label>Max demand (A)</Label><Input testid="eic-max-demand" value={formData.max_demand} onChange={set("max_demand")} /></div>
          <div className="form-field"><Label>Smoke alarms</Label><Input testid="eic-smoke-alarms" value={formData.num_smoke_alarms} onChange={set("num_smoke_alarms")} placeholder="N/A" /></div>
        </div>
      </div>

      <div className="flex justify-between">
        <button data-testid="eic-step-2-prev" onClick={onPrev} className="brutalist-btn-outline flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Previous</button>
        <button data-testid="eic-step-2-next" onClick={onNext} className="brutalist-btn flex items-center gap-2">Next <ArrowRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
